import React, { useState, useEffect } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  Button
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Check as CheckIcon,
  Speed as ComplexityIcon,
  Person as UserIcon,
  Link as DependencyIcon,
  AutoFixHigh as ImproveIcon,
  ContentCopy as CopyIcon,
  DeleteOutline as DeleteIcon,
  Info as ExplainIcon,
  InfoOutlined as InfoIcon,
  Assignment as FeatureIcon,
  AccountBox as UserStoryIcon,
  Architecture as ArchitectureIcon,
  Security as SecurityIcon,
  Code as TechIcon,
  BugReport as TestingIcon,
  Api as ApiIcon,
  CheckCircle as ValidationIcon,
  AccountCircle as RoleIcon,
  Timeline as ProcessIcon,
  Brush as WireframeIcon
} from '@mui/icons-material';
import { parseUniversalMarkdown, isValidMarkdown } from '../../utils/universalMarkdownParser';
import ReactMarkdown from 'react-markdown';

/**
 * Universal Accordion Component
 * Works with any task type and markdown format
 * Auto-detects content type and displays appropriately
 */
const UniversalAccordion = ({ 
  content, 
  taskType = null, 
  title = null,
  onImprove, 
  onExplain, 
  onCopy, 
  onDelete 
}) => {
  const [parsedContent, setParsedContent] = useState([]);
  const [detectedType, setDetectedType] = useState(null);
  const [validated, setValidated] = useState({});
  const [showRawData, setShowRawData] = useState(false);

  useEffect(() => {
    if (!content || typeof content !== 'string') {
      console.log('No content provided to UniversalAccordion');
      setParsedContent([]);
      return;
    }

    console.log('UniversalAccordion processing content:', content.length, 'characters');

    // Try to parse with universal parser
    const parsed = parseUniversalMarkdown(content, taskType);
    if (parsed && parsed.length > 0) {
      setParsedContent(parsed);
      setDetectedType(parsed[0].type);
      console.log(`Successfully parsed as ${parsed[0].type} with ${parsed.length} categories`);
    } else {
      // Fallback: create a simple content item
      setParsedContent([{
        title: title || 'Content',
        items: [{
          id: 'content-1',
          title: 'Imported Content',
          type: taskType || 'general',
          content: content,
          sections: {},
          metadata: {}
        }],
        type: taskType || 'general'
      }]);
    }
  }, [content, taskType, title]);

  // Get icon based on task type
  const getTaskIcon = (type) => {
    const icons = {
      'features': FeatureIcon,
      'user-stories': UserStoryIcon,
      'user-roles': RoleIcon,
      'architecture': ArchitectureIcon,
      'tech-stack': TechIcon,
      'security-guidelines': SecurityIcon,
      'unit-testing': TestingIcon,
      'api-guidelines': ApiIcon,
      'field-validations': ValidationIcon,
      'wireframes': WireframeIcon,
      'process-flows': ProcessIcon
    };
    return icons[type] || InfoIcon;
  };

  // Get color scheme based on task type
  const getTaskColor = (type) => {
    const colors = {
      'features': '#2196F3',
      'user-stories': '#4CAF50', 
      'user-roles': '#FF9800',
      'architecture': '#9C27B0',
      'tech-stack': '#607D8B',
      'security-guidelines': '#F44336',
      'unit-testing': '#3F51B5',
      'api-guidelines': '#009688',
      'field-validations': '#795548',
      'wireframes': '#E91E63',
      'process-flows': '#00BCD4'
    };
    return colors[type] || '#475969';
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    if (!priority) return 'default';
    
    const lower = priority.toLowerCase();
    if (lower.includes('must') || lower.includes('high') || lower.includes('critical')) return 'error';
    if (lower.includes('should') || lower.includes('medium') || lower.includes('important')) return 'warning';  
    if (lower.includes('could') || lower.includes('low') || lower.includes('nice')) return 'success';
    
    return 'default';
  };

  // Get complexity color
  const getComplexityColor = (complexity) => {
    if (!complexity) return 'default';
    
    const lower = complexity.toLowerCase();
    if (lower.includes('high') || lower.includes('complex')) return 'error';
    if (lower.includes('medium') || lower.includes('moderate')) return 'warning';
    if (lower.includes('low') || lower.includes('simple') || lower.includes('easy')) return 'success';
    
    return 'default';
  };

  // Render content sections
  const renderSections = (item) => {
    const sections = [];

    // Main content/description
    if (item.content && item.content.trim()) {
      sections.push(
        <Box key="description" sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            <ReactMarkdown>{item.content}</ReactMarkdown>
          </Typography>
        </Box>
      );
    }

    // Render additional sections
    Object.entries(item.sections || {}).forEach(([sectionName, sectionContent]) => {
      if (sectionContent && sectionContent.trim()) {
        sections.push(
          <Box key={sectionName} sx={{ mb: 2 }}>
            <Typography 
              variant="subtitle2" 
              color="primary" 
              gutterBottom
              sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}
            >
              {sectionName.replace(/-/g, ' ')}
            </Typography>
            <Box sx={{ ml: 1 }}>
              {sectionName.includes('criteria') || sectionName.includes('requirements') ? 
                // Render as list for criteria/requirements
                <List dense disablePadding>
                  {sectionContent.split('\n')
                    .filter(line => line.trim().length > 0)
                    .map((line, i) => (
                      <ListItem key={i} dense sx={{ py: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <CheckIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={line.replace(/^[-*]\s*/, '')} 
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))
                  }
                </List>
                :
                // Render as markdown for other content
                <Typography variant="body2" component="div">
                  <ReactMarkdown>{sectionContent}</ReactMarkdown>
                </Typography>
              }
            </Box>
          </Box>
        );
      }
    });

    return sections;
  };

  // If no parsed content, show info message
  if (parsedContent.length === 0) {
    return (
      <Box sx={{ mt: 2 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          No structured content found in the import.
        </Alert>
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button 
            variant="outlined" 
            size="small"
            onClick={() => setShowRawData(!showRawData)}
            startIcon={<InfoIcon />}
          >
            {showRawData ? 'Hide Raw Data' : 'Show Raw Data'}
          </Button>
        </Box>
        
        {showRawData && (
          <Paper sx={{ p: 2, maxHeight: '400px', overflow: 'auto' }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Raw Content:
            </Typography>
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>
              {content}
            </pre>
          </Paper>
        )}
      </Box>
    );
  }

  // Render the parsed content
  return (
    <Box sx={{ mt: 2 }}>
      {parsedContent.map((category, categoryIndex) => {
        const TaskIcon = getTaskIcon(category.type);
        const taskColor = getTaskColor(category.type);
        
        return (
          <Box key={categoryIndex} sx={{ mb: 4 }}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 1.5,
                pl: 3,
                mb: 2,
                borderLeft: '4px solid',
                borderColor: taskColor,
                backgroundColor: 'rgba(0, 0, 0, 0.03)',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center', 
                justifyContent: 'space-between'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TaskIcon sx={{ color: taskColor }} />
                <Typography 
                  variant="h5" 
                  sx={{ fontWeight: 500, color: taskColor }}
                >
                  {category.title}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip 
                  label={category.type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())} 
                  size="small" 
                  sx={{ backgroundColor: taskColor, color: 'white' }}
                />
                <Chip 
                  label={`${category.items.length} item${category.items.length !== 1 ? 's' : ''}`} 
                  color="primary" 
                  size="small" 
                  variant="outlined"
                />
              </Box>
            </Paper>
            
            {category.items.length === 0 ? (
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(0,0,0,0.02)' }}>
                <Typography color="text.secondary">
                  No items found.
                </Typography>
              </Paper>
            ) : (
              category.items.map((item, itemIndex) => (
                <Accordion 
                  key={itemIndex}
                  defaultExpanded={categoryIndex === 0 && itemIndex === 0}
                  sx={{ 
                    mb: 1,
                    '&:before': { display: 'none' },
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ 
                      backgroundColor: taskColor,
                      color: 'white',
                      borderRadius: '4px',
                      borderLeft: '3px solid',
                      borderColor: (theme) => 
                        validated[`${categoryIndex}-${itemIndex}`] 
                          ? theme.palette.success.main :
                        getPriorityColor(item.metadata.priority) === 'error' 
                          ? theme.palette.error.main :
                        getPriorityColor(item.metadata.priority) === 'warning' 
                          ? theme.palette.warning.main :
                        getPriorityColor(item.metadata.priority) === 'success' 
                          ? theme.palette.success.main :
                        theme.palette.grey[400],
                      '&.Mui-expanded': {
                        borderBottom: '1px solid rgba(0, 0, 0, 0.1)', 
                      }
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      width: '100%',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {validated[`${categoryIndex}-${itemIndex}`] && (
                          <Box 
                            sx={{ 
                              mr: 1.5,
                              backgroundColor: 'success.main', 
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 24,
                              height: 24
                            }}
                          >
                            <CheckIcon sx={{ color: 'white', fontSize: '1rem' }} />
                          </Box>
                        )}
                        <Typography 
                          variant="body2" 
                          sx={{ fontWeight: 500, color: 'white' }}
                        >
                          {item.code ? `${item.code}: ${item.title}` : item.title}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {!validated[`${categoryIndex}-${itemIndex}`] && (
                          <Tooltip title="Mark as Validated">
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                setValidated(prev => ({ 
                                  ...prev, 
                                  [`${categoryIndex}-${itemIndex}`]: true 
                                }));
                              }}
                              sx={{ color: 'white' }}
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {item.metadata.priority && (
                          <Chip 
                            label={item.metadata.priority}
                            size="small"
                            color={getPriorityColor(item.metadata.priority)}
                            sx={{ height: 20, fontSize: '0.75rem' }}
                          />
                        )}
                        {item.metadata.complexity && (
                          <Chip 
                            label={item.metadata.complexity}
                            size="small"
                            color={getComplexityColor(item.metadata.complexity)}
                            sx={{ height: 20, fontSize: '0.75rem' }}
                            icon={<ComplexityIcon sx={{ fontSize: '0.75rem !important' }} />}
                          />
                        )}
                      </Box>
                    </Box>
                  </AccordionSummary>
                  
                  <AccordionDetails sx={{ p: 2, position: 'relative' }}>
                    {/* Action buttons */}
                    <Box 
                      sx={{ 
                        position: 'absolute', 
                        top: 12, 
                        right: 16, 
                        display: 'flex',
                        gap: 1
                      }}
                    >
                      <Tooltip title="Improve">
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onImprove) onImprove(item, categoryIndex, itemIndex);
                          }}
                        >
                          <ImproveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Explain">
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onExplain) onExplain(item, categoryIndex, itemIndex);
                          }}
                        >
                          <ExplainIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Copy">
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onCopy) {
                              onCopy(item, categoryIndex, itemIndex);
                            } else {
                              navigator.clipboard.writeText(
                                JSON.stringify(item, null, 2)
                              );
                            }
                          }}
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Delete">
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onDelete) onDelete(item, categoryIndex, itemIndex);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {/* Content sections */}
                    <Box sx={{ pr: 6 }}> {/* Leave space for action buttons */}
                      {renderSections(item)}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default UniversalAccordion;